/**
 * Global axios interceptor for ASCIN multi-tenant auth.
 *
 * Runs at app boot. For every outgoing request to one of our
 * tenant-scoped APIs, attaches `Authorization: Bearer <ascin_token>`
 * if a token is present in localStorage. On 401, clears the token
 * and bounces the user to /ascin-login.
 *
 * Coexists with Firebase auth: only acts on `/api/v1/*` and only
 * sets the header if it's not already set by another interceptor.
 */
import axios from 'axios';

const TOKEN_KEY = 'ascin_token';

let installed = false;

export function installAscinAuthInterceptor() {
  if (installed) return;
  installed = true;

  axios.interceptors.request.use((config) => {
    try {
      const url = (config.url || '') + '';
      const isApi = url.includes('/api/v1/');
      if (!isApi) return config;
      // Don't override if caller already set Authorization
      const hasAuth =
        config.headers &&
        ((config.headers as any).Authorization || (config.headers as any).authorization);
      if (hasAuth) return config;
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        config.headers = config.headers || {};
        (config.headers as any).Authorization = `Bearer ${token}`;
      }
    } catch {
      /* noop */
    }
    return config;
  });

  axios.interceptors.response.use(
    (r) => r,
    (error) => {
      const status = error?.response?.status;
      const url = (error?.config?.url || '') + '';
      if (status === 401 && url.includes('/api/v1/') && !url.includes('/ascin-auth/')) {
        // Token expired or revoked — fall back to login
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem('ascin_user');
        localStorage.removeItem('ascin_tenant');
        if (window.location.pathname !== '/ascin-login') {
          window.location.href = '/ascin-login';
        }
      }
      return Promise.reject(error);
    }
  );
}
